/**
 * Product navigation tree: groups, modules and sub-views.
 *
 * It used to be written inside `Layout.tsx`, so only the side menu knew where each
 * screen fit. Breadcrumbs need the same thing -- group, module, sub-view -- and while
 * that wasn't shared, every screen had to declare its own route by hand: that's why
 * only four screens had breadcrumbs and the rest didn't.
 *
 * It lives here once. Consumed by `Layout.tsx` and `useRouteBreadcrumbs`.
 */
import React from 'react';
import {
  FileText,
  Home,
  Package,
  Landmark,
  CalendarClock,
  FileCheck2,
  Calculator,
  TrendingUp,
  PieChart,
  DollarSign,
  Settings,
  Users,
  Briefcase,
  ShieldCheck,
  Bell,
  ShieldAlert,
  User as UserIcon,
  Database,
  FolderCog,
  Layers,
  LayoutDashboard,
  Wallet,
  Percent,
  Scale,
  AlertTriangle,
  Lock,
  Sliders,
  Palette,
  Shield,
  Link,
  CreditCard,
  MessageCircle,
  UserCheck,
  FileCheck,
  Mail,
  Clock,
  CheckCircle2,
  Receipt,
  HandCoins,
  FileArchive,
  Handshake,
  Store,
} from 'lucide-react';
import { ROUTES, ROUTE_PERMISSIONS, BILLING_ROUTES } from '../routes';
import { OPERATIONS_MODULE_ENABLED } from './featureFlags';
import { accountingSidebarSubItems } from './accountingNav';
import { operationsSidebarSubItems } from './operationsNav';
import { quoteSidebarSubItems } from './quoteNav';
import { portalSidebarSubItems } from './portalNav';

export interface SubNavItem {
  id: string;
  labelKey: string;
  icon?: React.ComponentType<{ size?: number; className?: string; style?: React.CSSProperties }>;
  /**
   * Label of the group this sub-item opens. Rendered above it, and groups together up to
   * the next sub-item that carries one.
   *
   * Born from Accounting (18/08): its ten submodules were already grouped by work
   * affinity, but the grouping lived INSIDE the screen, in a strip of pills that repeated
   * the left-hand menu and took up half the view. Grouping is navigation information: it
   * belongs in the menu.
   */
  groupLabelKey?: string;
  /** One-line purpose, shown as tooltip / accessible description. */
  descriptionKey?: string;
  /** If set, this sub-item is hidden unless the user has one of these roles. */
  roles?: string[];
}

export interface NavItem {
  id: string;
  labelKey: string;
  icon: React.ComponentType<{ size?: number; className?: string; style?: React.CSSProperties }>;
  roles: string[];
  highlight?: boolean;
  subItems?: SubNavItem[];
  /** Module finished but off by business decision — show "Coming soon" in nav when set. */
  comingSoon?: boolean;
}

export interface NavGroup {
  labelKey: string;
  items: NavItem[];
}

export const NAV_GROUPS: NavGroup[] = [
  {
    labelKey: 'nav.groups.operation',
    items: [
      { id: ROUTES.home, labelKey: 'nav.items.home', icon: Home, roles: ROUTE_PERMISSIONS.home },
 // Reduced agency interface (design spec §3.2/§3.5): the only screen an agencia_operador
 // sees besides Home -- every other item below is filtered out by its own `roles`.
      { id: ROUTES.agencyPortal, labelKey: 'nav.items.agencyPortal', icon: Store, roles: ROUTE_PERMISSIONS.agencyPortal },
      // Reordered (15/08) to follow the real flow of an operations day: check the mailbox
      // first, then coordinate pickups, and only after that quote/work tracking numbers --
      // the order used to put Quote/Operations ahead of Mailbox/Pickups, inverting the
      // actual priority (see the operations meeting cited under pickups below). It doesn't
      // change what each role sees (ROUTE_PERMISSIONS still decides that), only the
      // relative order. `comercial` doesn't see Mailbox/Pickups/Cases, so for that role
      // Quote is still the first thing after Home, no flow change.
      //
      // A single item visible to both operations and accounting -- each one sees only
      // THEIR mailbox inside the page (fine-grained gating in Buzon.tsx/the backend's
      // MAILBOX_ROLES), no need to duplicate the menu entry per role.
      { id: ROUTES.mailbox, labelKey: 'nav.items.mailbox', icon: Mail, roles: ROUTE_PERMISSIONS.mailbox },
      // Pickups (11/08): its own screen because the operator checks it daily "the next
      // day" to see if the driver picked up, independent of whether they're editing an
      // operation.
      { id: ROUTES.pickups, labelKey: 'nav.items.pickups', icon: CalendarClock, roles: ROUTE_PERMISSIONS.pickups },
      {
        id: ROUTES.quote,
        labelKey: 'nav.items.quote',
        icon: TrendingUp,
        roles: ROUTE_PERMISSIONS.quote,
        subItems: quoteSidebarSubItems(),
      },
      { id: ROUTES.customerFollowup, labelKey: 'nav.items.customerFollowup', icon: UserCheck, roles: ROUTE_PERMISSIONS.customerFollowup },
      { id: ROUTES.myWhatsappChats, labelKey: 'nav.items.myWhatsappChats', icon: MessageCircle, roles: ROUTE_PERMISSIONS.myWhatsappChats },
      ...(OPERATIONS_MODULE_ENABLED
        ? [{
            id: ROUTES.operations,
            labelKey: 'nav.items.operations',
            icon: Package,
            roles: ROUTE_PERMISSIONS.operations,
            subItems: operationsSidebarSubItems(),
          }]
        : []),
      // Cases and disputes with carriers (13/08, Task 2)
      { id: ROUTES.cases, labelKey: 'nav.items.cases', icon: Shield, roles: ROUTE_PERMISSIONS.cases },
      // Client Portal last (15/08): it's the external CLIENT's view, not a tool of the
      // internal operational flow -- confirmed in ROUTE_PERMISSIONS: only
      // adminapp/admin/cliente see it; neither operaciones nor comercial have any access
      // to this entry at all, so moving it changes nothing for them. For the `cliente`
      // role, who only sees Home + this item in the group, the visible order doesn't
      // change either (still their only two entries). Only affects the order admin/
      // adminapp see, since they have the full menu.
      {
        id: ROUTES.clientPortal,
        labelKey: 'nav.items.clientPortal',
        icon: LayoutDashboard,
        roles: ROUTE_PERMISSIONS.clientPortal,
        subItems: portalSidebarSubItems(),
      },
    ],
  },
  {
    labelKey: 'nav.groups.financial',
    items: [
      {
        id: ROUTES.accounting,
        labelKey: 'nav.items.accounting',
        icon: Calculator,
        roles: ROUTE_PERMISSIONS.accounting,
        // Single catalog: `src/lib/accountingNav.ts`. Side menu, page header, desk map
        // and sibling rail all read from there so a destination never has two names.
        subItems: accountingSidebarSubItems(),
      },
      {
        id: ROUTES.billingReport,
        labelKey: 'nav.items.billingReport',
        icon: FileText,
        roles: ROUTE_PERMISSIONS.billingReport,
        subItems: [
          // 13 → 4 (10/08, PLAN_SANEAMIENTO_SISTEMA.md Fase 3). The previous 13 entries
          // pointed to views of the SAME dataset; the ones that contributed something
          // different still exist as sub-tabs within their section. `comisiones` moved
          // to /commissions (it had a different data source, see Fase 2) and `config` to
          // /configuracion.
          { id: `${ROUTES.billingReport}/${BILLING_ROUTES.dashboard}`, labelKey: 'billing.shell.tabs.dashboard', icon: LayoutDashboard },
          { id: `${ROUTES.billingReport}/${BILLING_ROUTES.billing}`, labelKey: 'billing.shell.tabs.billing', icon: FileText },
          { id: `${ROUTES.billingReport}/${BILLING_ROUTES.rankings}`, labelKey: 'billing.shell.tabs.rankings', icon: TrendingUp },
          { id: `${ROUTES.billingReport}/${BILLING_ROUTES.control}`, labelKey: 'billing.shell.tabs.control', icon: ShieldCheck },
        ],
      },
      {
        id: ROUTES.commissions,
        labelKey: 'nav.items.commissions',
        icon: DollarSign,
        highlight: true,
        roles: ROUTE_PERMISSIONS.commissions,
        subItems: [
          { id: '/commissions?status=all', labelKey: 'commissions.allRecords', icon: DollarSign },
          { id: '/commissions?status=pending_payment', labelKey: 'commissions.pendingPayment', icon: Clock },
          { id: '/commissions?status=pending_commission', labelKey: 'commissions.readyToSettle', icon: CheckCircle2 },
          { id: '/commissions?status=liquidated', labelKey: 'commissions.settled', icon: Wallet },
          { id: '/commissions?view=porcentajes', labelKey: 'commissions.rates.title', icon: Percent, roles: ['adminapp', 'admin', 'ceo', 'accounting'] },
        ],
      },
      { id: ROUTES.dashboard, labelKey: 'nav.items.dashboard', icon: PieChart, roles: ROUTE_PERMISSIONS.dashboard },
    ],
  },
  {
    labelKey: 'nav.groups.administration',
    items: [
      { id: ROUTES.clients, labelKey: 'nav.items.clients', icon: Users, roles: ROUTE_PERMISSIONS.clients },
      { id: ROUTES.salesReps, labelKey: 'nav.items.salesReps', icon: Briefcase, roles: ROUTE_PERMISSIONS.salesReps },
 // Partners API + agency network (design spec §2.4/§3.5): admin-only config, same group as
 // the rest of "who can operate against this tenant" management.
      { id: ROUTES.partners, labelKey: 'nav.items.partners', icon: Handshake, roles: ROUTE_PERMISSIONS.partners },
      { id: ROUTES.agencies, labelKey: 'nav.items.agencies', icon: Store, roles: ROUTE_PERMISSIONS.agencies },
      { id: ROUTES.whatsappAgent, labelKey: 'nav.items.whatsappAgent', icon: MessageCircle, roles: ROUTE_PERMISSIONS.whatsappAgent },
      { id: ROUTES.users, labelKey: 'nav.items.users', icon: Users, roles: ROUTE_PERMISSIONS.users },
      {
        id: ROUTES.audit,
        labelKey: 'nav.items.audit',
        icon: ShieldCheck,
        roles: ROUTE_PERMISSIONS.audit,
        subItems: [
          { id: `${ROUTES.audit}?tab=logs`, labelKey: 'audit.tabLogs', icon: ShieldCheck },
          { id: `${ROUTES.audit}?tab=blocked_ips`, labelKey: 'audit.tabBlockedIps', icon: Lock },
          { id: `${ROUTES.audit}?tab=anomalies`, labelKey: 'audit.tabAnomalies', icon: AlertTriangle },
          { id: `${ROUTES.audit}?tab=prices`, labelKey: 'audit.tabPrices', icon: DollarSign },
        ],
      },
      {
    id: ROUTES.backup,
    labelKey: 'nav.items.backup',
    icon: Database,
    roles: ROUTE_PERMISSIONS.backup,
    subItems: [
      { id: '/backup?view=datos', labelKey: 'backup.tabs.data', icon: Database },
      { id: '/backup?view=configuracion', labelKey: 'backup.tabs.config', icon: FolderCog },
      { id: '/backup?view=usuarios', labelKey: 'backup.tabs.users', icon: Layers },
      // Initial migration (19/08): it's done once, but until it's done it's the most
      // important thing on this screen, so it has to be easy to find.
      { id: '/backup?view=migracion', labelKey: 'backup.tabs.migration', icon: Database },
    ],
  },
      {
        id: ROUTES.settings,
        labelKey: 'nav.items.settings',
        icon: Settings,
        roles: ROUTE_PERMISSIONS.settings,
        subItems: [
        { id: `${ROUTES.settings}?tab=empresa`, labelKey: 'settings.tabs.empresa', icon: Sliders },
        { id: `${ROUTES.settings}?tab=appearance`, labelKey: 'settings.tabs.appearance', icon: Palette },
        { id: `${ROUTES.settings}?tab=integraciones`, labelKey: 'settings.tabs.integraciones', icon: Link },
        { id: `${ROUTES.settings}?tab=comunicaciones`, labelKey: 'settings.tabs.comunicaciones', icon: MessageCircle },
        { id: `${ROUTES.settings}?tab=sistema`, labelKey: 'settings.tabs.sistema', icon: Shield },
      ],
      },
      {
        id: ROUTES.profile,
        labelKey: 'nav.items.profile',
        icon: UserIcon,
        roles: ROUTE_PERMISSIONS.profile,
        subItems: [
          { id: `${ROUTES.profile}?seccion=cuenta`, labelKey: 'profile.sections.cuenta', icon: UserIcon },
          { id: `${ROUTES.profile}?seccion=seguridad`, labelKey: 'profile.sections.seguridad', icon: Shield },
          { id: `${ROUTES.profile}?seccion=avisos`, labelKey: 'profile.sections.avisos', icon: Bell },
          { id: `${ROUTES.profile}?seccion=documents`, labelKey: 'profile.sections.documents', icon: FileCheck, roles: ['client'] },
          { id: `${ROUTES.profile}?seccion=privacidad`, labelKey: 'profile.sections.privacidad', icon: Lock },
        ],
      },
    ],
  },
];
